/*
 * exportChartPdf.js
 * ---------------------------------------------------------------------------
 * Exportacao de graficos para PDF, do lado do cliente.
 *
 * Compomos o PDF a partir da imagem do <canvas> do Chart.js (toBase64Image)
 * e desenhamos o titulo, o periodo de analise e a legenda de forma
 * programatica -- assim o texto fica nitido e garantimos que o PDF inclui
 * SEMPRE titulo, legenda e periodo, tal como pedido.
 *
 * DEPENDENCIA: jsPDF. Adicionar ao projecto com:  npm install jspdf
 * ---------------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';

/* Margens e medidas base, em pontos (unidade 'pt' do jsPDF). */
const MARGIN = 40;
const SWATCH = 18;
const LINE_H = 16;

/* '#2563EB' -> { r, g, b }. Aceita tambem 'rgb(...)' por seguranca. */
const HexToRgb = (hex) => {
	if (!hex) {
		return { r: 100, g: 100, b: 100 };
	}
	if (hex.startsWith('rgb')) {
		const parts = hex.replace(/[^\d,]/g, '').split(',').map(Number);
		return { r: parts[0] || 0, g: parts[1] || 0, b: parts[2] || 0 };
	}
	const value = hex.replace('#', '');
	return {
		r: parseInt(value.substring(0, 2), 16),
		g: parseInt(value.substring(2, 4), 16),
		b: parseInt(value.substring(4, 6), 16)
	};
};

/* Nome de ficheiro seguro a partir do titulo e do periodo. */
const SafeFileName = (title, period) => {
	const base = `${title || 'chart'}_${period || ''}`;
	return `${base.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 120) || 'chart'}.pdf`;
};

/*
 * Desenha um gráfico numa pagina do documento (assume que a pagina ja existe).
 * entry = { canvas, title, subtitle, period, legend }
 *   canvas -> o elemento <canvas> do grafico (Chart.js)
 *   legend = [{ label, color, dashed }]
 */
const RenderPage = (doc, entry) => {
	const { canvas, title, subtitle, period, legend = [] } = entry;

	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const contentWidth = pageWidth - MARGIN * 2;
	let y = MARGIN;

	/* Titulo. */
	doc.setFont('helvetica', 'bold');
	doc.setFontSize(16);
	doc.setTextColor(31, 41, 55);
	doc.text(String(title || 'Chart'), MARGIN, y);
	y += 20;

	/* Subtitulo: periodo de analise + granularidade. */
	if (subtitle) {
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(10);
		doc.setTextColor(107, 114, 128);
		doc.text(String(subtitle), MARGIN, y);
		y += 16;
	}

	/* Imagem do grafico, escalada a largura util, preservando o aspect ratio. */
	if (canvas && canvas.width && canvas.height) {
		const dataUrl = canvas.toDataURL('image/png');
		const ratio = canvas.height / canvas.width;
		let imgWidth = contentWidth;
		let imgHeight = imgWidth * ratio;

		/* Reserva espaco para a legenda (aprox.) e nunca ultrapassa a pagina. */
		const legendRows = Math.ceil(legend.length / 3);
		const legendHeight = legend.length ? (legendRows * LINE_H + 24) : 0;
		const maxImgHeight = pageHeight - y - MARGIN - legendHeight;
		if (imgHeight > maxImgHeight) {
			imgHeight = maxImgHeight;
			imgWidth = imgHeight / ratio;
		}

		/* Fundo branco por baixo, para o caso de o canvas ser transparente. */
		doc.setFillColor(255, 255, 255);
		doc.rect(MARGIN, y, imgWidth, imgHeight, 'F');
		doc.addImage(dataUrl, 'PNG', MARGIN, y, imgWidth, imgHeight);
		y += imgHeight + 20;
	}

	/* Legenda: uma amostra de cor/linha por serie visivel, em 3 colunas. */
	if (legend.length) {
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(11);
		doc.setTextColor(31, 41, 55);
		doc.text('Legend', MARGIN, y);
		y += 14;

		doc.setFont('helvetica', 'normal');
		doc.setFontSize(9);
		doc.setTextColor(55, 65, 81);

		const cols = 3;
		const colWidth = contentWidth / cols;
		legend.forEach((item, index) => {
			const col = index % cols;
			const row = Math.floor(index / cols);
			const x = MARGIN + col * colWidth;
			const rowY = y + row * LINE_H;

			const { r, g, b } = HexToRgb(item.color);
			doc.setDrawColor(r, g, b);
			doc.setLineWidth(2);
			if (item.dashed) {
				doc.setLineDashPattern([3, 2], 0);
			} else {
				doc.setLineDashPattern([], 0);
			}
			doc.line(x, rowY - 3, x + SWATCH, rowY - 3);
			doc.setLineDashPattern([], 0);

			const label = String(item.label);
			const maxChars = Math.floor((colWidth - SWATCH - 12) / 4.6);
			const shown = label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label;
			doc.text(shown, x + SWATCH + 6, rowY);
		});
	}

	void period;
};

/* Exporta UM gráfico para um PDF de uma pagina. */
export const ExportChartToPdf = (entry) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
	RenderPage(doc, entry);
	doc.save(SafeFileName(entry.title, entry.period));
};

/* Exporta varios graficos para um unico PDF, um por pagina. */
export const ExportChartsToPdf = (entries, fileName) => {
	const list = (entries || []).filter(entry => entry && entry.canvas);
	if (!list.length) {
		return;
	}
	const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
	list.forEach((entry, index) => {
		if (index > 0) {
			doc.addPage();
		}
		RenderPage(doc, entry);
	});
	doc.save(fileName || 'kpis_overview.pdf');
};